import {DatePipe} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, Output, Signal, SimpleChanges, computed, effect, linkedSignal} from '@angular/core';
import {CartTypes, RouterLinks} from '../../constants';
import {FlowPageDataProduct, FlowSummary, QuestionFlowCollation} from '../../models/quote.model';
import {ShoppingCartQuery} from '../../questions/state/shopping-cart-query';
import {GlobalQuestionService} from '../../services/global-question.service';
import {QuestionFlowSessionQuery} from '../../questions/state/question-flow-session.query';
import {ShoppingCartStore} from '../../questions/state/shopping-cart-store';
import {Router} from '@angular/router';
import {Question, ShoppingCartItem} from '../../models/underwriter.model';

declare var $: any;

interface SidebarData {
  event_details: any;
  groups: any[];
}

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: false
})
export class SidebarComponent implements OnChanges {

  @Input() question: Question;
  @Output() showEditModal = new EventEmitter<any>();

  /** Reactive read access — the store is the single source of truth, no manual "refresh the sidebar" calls needed. */
  readonly cartItems: Signal<ShoppingCartItem[]> = this.shoppingCartQuery.items;
  readonly activeItem: Signal<ShoppingCartItem | undefined> = this.shoppingCartQuery.active;
  readonly sessionDetails = this.questionFlowSessionQuery.session;

  readonly cartType = computed(() => this.sessionDetails()?.flow_type_id === 2 ? CartTypes.claim : CartTypes.acquisition);
  readonly sidebarData: Signal<SidebarData> = computed(() => this.buildSidebarData(this.cartItems()));

  /** Item the user clicked — a visual-only highlight that hands back to the store's active item on the next cart update. */
  private readonly clickedItemId = linkedSignal<SidebarData, number | null>({source: this.sidebarData, computation: () => null});

  // Template still reads plain properties (`groups`, `event_details`, `cart_type`) — these getters
  // keep it derived from the signals above without touching every binding in sidebar.component.html.
  get groups(): any[] {
    return this.sidebarData().groups;
  }

  get event_details(): any {
    return this.sidebarData().event_details;
  }

  get cart_type(): CartTypes {
    return this.cartType();
  }

  CartType = CartTypes;
  selectedItem: any;
  collations: QuestionFlowCollation[] = [];
  summaryCollations: QuestionFlowCollation[] = [];
  product: FlowPageDataProduct;
  summary = new FlowSummary();
  RouterLinks = RouterLinks;

  constructor(
    private shoppingCartQuery: ShoppingCartQuery,
    private shoppingCartStore: ShoppingCartStore,
    public questionService: GlobalQuestionService,
    private questionFlowSessionQuery: QuestionFlowSessionQuery,
    private datePipe: DatePipe,
    private router: Router,
    private el: ElementRef
  ) {
    effect(() => {
      // Re-run popover init / scroll-to-selected whenever the derived sidebar data changes.
      this.sidebarData();
      setTimeout(() => {
        $('[data-toggle="popover"]').popover();
        this.el.nativeElement.querySelector('.progress-tree__detail--selected')?.scrollIntoView({behavior: 'smooth', block: 'start'});
      }, 500);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.question?.previousValue !== changes?.question?.currentValue) {
      this.question = changes?.question?.currentValue;
    }
  }

  getItemStatus(progress: any, hasSkipped: any): string {
    if (progress < 1) {
      return 'pending';
    } else {
      if (hasSkipped === 1) {
        return 'pending';
      } else {
        return 'complete';
      }
    }
  }

  formatItemDescription(description: string, type: number): string {
    const splitDescription = description?.split('|');
    if (splitDescription) {
      switch (type) {
        case 1:
          return splitDescription[0];
        case 2:
          return splitDescription[1];
        default:
          return '';
      }
    }
    return '';
  }

  formatGroup(group: string): any {
    return this.camelize(this.replaceUnderscores(group));
  }

  replaceUnderscores(str: string): any {
    return str.replace(/_/g, ' ');
  }

  camelize(s: string): any {
    return s.replace(/\w+/g,
      (w: any) => w[0].toUpperCase() + w.slice(1).toLowerCase());
  }

  buildTooltip(tip: any): any {
    let tips = '';
    try {
      JSON.parse(tip).forEach(t => {
        tips += t.item_name + ' ' + this.questionService.accounting?.formatMoney(t.item_value) + ' /' + t.frequency;
      });
    } catch {
    }

    return tips;
  }

  private findEntryType(items: ShoppingCartItem[], entry_type: string): ShoppingCartItem[] {
    return items?.filter((entry: any) => entry?.entry_type === entry_type) ?? [];
  }

  getEntryType(entry_type: string): any {
    return this.findEntryType(this.cartItems(), entry_type);
  }

  private buildSidebarData(cartItems: ShoppingCartItem[]): SidebarData {
    let event_details: any = null;
    const groups: any[] = [];

    const groupsDict: {} = cartItems?.reduce((r: any, a: any) => {
      r[a.class_id] = [...r[a.class_id] || [], a];
      return r;
    }, {});

    const groupsArray = Object.entries(groupsDict ?? {});

    groupsArray?.forEach((item: any) => {
      const entries = item[1];

      entries?.forEach((entry: any) => {
        const stageName = entry?.entry_type;

        let status = 'inactive';
        if (entry.active === 1) {
          status = 'active';
        }
        if (entry.progress === 1) {
          status = 'complete';
        }
        switch (stageName) {
          case 'event_details': {
            const items = [];
            const event_description = entry?.description?.split('|');
            if (event_description) {
              let location: any;
              let date: any;
              const name = event_description[0];
              const dateSplit = event_description[2]?.split('-');

              if (dateSplit?.length === 3) {
                const year = dateSplit[0];
                const month = dateSplit[1];
                const day = dateSplit[2];

                const startDate = new Date(+year, +month - 1, +day);
                if (!isNaN(startDate.getTime())) {
                  date = this.datePipe.transform(startDate, 'MMM dd, yyyy');
                }
              }

              if (event_description[1]) {
                try {
                  const address = JSON.parse(event_description[1]);
                  location = `${address.streetAddress}, ${address.suburb}, ${address.city}, ${address.postalCode}`;
                } catch (e) {
                  location = event_description[1];
                }
              }

              items.push({name, location, date});
            }
            event_details = {
              group_type: this.findEntryType(cartItems, 'event_details_header')[0]?.description,
              entry_type: stageName, items, status, percentage: `${entry.progress * 100}%`
            };
            break;
          }
          case 'class_header': {
            const items = this.getItemsByClassId(entries, entry.class_id);
            groups.push({group_type: stageName, group_class: entry.class_id, group_name: entry.class_name, items, status, percentage: `${entry.progress * 100}%`});
            break;
          }
          case 'personal_details': {
            event_details = {
              group_type: this.findEntryType(cartItems, 'personal_header')[0]?.description,
              description: entry.description,
              items: [],
              status, percentage: `${entry.progress * 100}%`
            } as any;
            break;
          }
          default:
            break;
        }
      });
    });

    return {event_details, groups};
  }

  getItemsByClassId(entries: any[], class_id: number): any {
    return entries?.filter((entry: any) =>
      entry.entry_type === 'class_item' && entry.class_id === class_id);
  }

  getPercentage(value: number): any {
    return `${value * 100}%`;
  }

  getStages(): any {
    return [
      {stage_name: 'Event Details'},
      {stage_name: 'Damage Report'},
      {stage_name: 'Police Report'}
    ];
  }

  isActive(item: any): any {
    const activeId = this.clickedItemId() ?? this.activeItem()?.shoppingcart_id;
    return activeId === item.shoppingcart_id;
  }

  getTotalPremium(groups: any[]): any {
    const items: any[] = [];
    groups.forEach((group: any) => {
      group.items.forEach((item: any) => {
        items.push(item);
      });
    });

    let sum = 0;
    items.forEach((item: any) => {
      sum += item.premium;
    });
    return sum;
  }

  activeClass(item: any): any {
    return this.isActive(item) === true ? ' is-active' : '';
  }

  activateItem(item: any): any {
    // this.itemStore.setActive(item.id);
    this.clickedItemId.set(item.shoppingcart_id);
  }

  selectDetailToggleHandler($el: any): void {
    const correctTarget = $el.hasClass('progress-tree__detail');
    if (!correctTarget) {
      return;
    }

    const $current = $('.progress-tree__detail--selected');

    if ($current.length > 0) {
      for (const item of $current) {
        item.classList.remove('is-active');
      }
    }

    if ($el.hasClass('progress-tree__detail')) {
      $el.addClass('is-active');
      // claimsFeedScrollToSelected($el)
    } else {
      $el.closest('.progress-tree__detail').addClass('is-active');
      // claimsFeedScrollToSelected($el.closest('.progress-tree__detail'))
    }
  }

  initFeedClickEventListeners(): any {
    const $divs = $('.progress-tree__detail');
    for (const item of $divs) {
      item.addEventListener('click', (e) => {
        this.selectDetailToggleHandler($(e.target));
      });
    }
  }

  editItem(item: any): void {
    this.questionService.spinner(true);
    if (this.cart_type === CartTypes.claim) {
      this.showEditModal.emit({cart_type: this.cart_type});
      setTimeout((e: any) => {
        this.questionService.spinner(false);
      }, 1000);
    } else {
      let collation: any;
      this.questionService.updatePrices(this.question?.product_id, this.question?.product_class_id, 0)
        .subscribe(x => {
          this.questionService.getPageData('quote')
            .subscribe(dataset => {

              this.product = this.questionService.getIndexData(4, dataset)[0];
              this.summary.pageDataProduct = this.product;
              this.summary.person = this.questionService.getIndexData(1, dataset)[0];
              this.collations = this.questionService.getIndexData(2, dataset);
              collation = this.collations.find(c => c.class_id === item?.class_id && c.session_item_id.toString() === item?.item_id.toString() && c.product_id === this.product?.product_id);

              console.log('item', item);
              console.log('collation', collation);
              console.log('collations', this.collations);

              this.showEditModal.emit({
                collations: this.collations,
                collation,
                product: this.product,
                person: this.summary?.person,
                path: '3'
              });
            }, error => {
              console.log(error);
              this.questionService.spinner(false);
            });
        }, () => {
          this.questionService.spinner(false);
        });
    }
  }

  editClaimsItem(item: any): void {
    if (item) {
      this.shoppingCartStore.setActive(item?.shoppingcart_id);
      this.router.navigate([`/${RouterLinks.ClaimSummaryUpdate}`]);
    }
  }

}
